import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, FinShell, NumField, SelectField, Stat, SavedBar, Assumptions, money, fmtDate } from "@/components/finance/fin";
import { PaymentHistory } from "@/components/finance/LoanParts";

const CATS = [["emergency", "Emergency fund"], ["vacation", "Vacation"], ["vehicle", "New vehicle"], ["home", "Home down payment"], ["education", "Education"], ["general", "General savings"]];
const inYear = () => { const d = new Date(); d.setFullYear(d.getFullYear() + 1); return d.toISOString().slice(0, 10); };
const DEFAULTS = { category: "emergency", target: 15000, current: 2000, target_date: inYear(), frequency: "monthly", rate: 3, contribution: "" };

function Progress({ pct }) {
  return (
    <div data-testid="savings-progress">
      <div className="flex justify-between text-xs text-muted-foreground mb-1"><span>Progress</span><span>{pct}%</span></div>
      <div className="h-4 rounded-full bg-secondary overflow-hidden"><div className="h-full bg-gradient-to-r from-primary to-emerald-400 transition-[width] duration-700" style={{ width: `${pct}%` }} /></div>
    </div>
  );
}

export default function SavingsPage() {
  const [i, setI] = useState(DEFAULTS);
  const [pid, setPid] = useState(null);
  const prof = useQuery({ queryKey: ["fin-profile", pid], queryFn: async () => (await api.get(`/finance/profiles/${pid}`)).data, enabled: !!pid });
  const txns = prof.data?.transactions || [];
  const contributed = txns.reduce((a, t) => a + t.amount, 0);
  const raw = JSON.stringify({ ...i, current: Number(i.current || 0) + contributed });
  const [body, setBody] = useState(raw);
  useEffect(() => { const t = setTimeout(() => setBody(raw), 350); return () => clearTimeout(t); }, [raw]);
  const calc = useQuery({ queryKey: ["fin-calc-savings", body], queryFn: async () => (await api.post("/finance/calc/savings", { inputs: JSON.parse(body) })).data, retry: false, placeholderData: (p) => p });
  const set = (k) => (v) => setI({ ...i, [k]: v });
  const r = calc.data;
  const per = { weekly: "week", biweekly: "2 weeks", monthly: "month" }[i.frequency];
  return (
    <FinShell title="Savings goals" subtitle="Work out how much to put away to reach a target by a date, and track your progress." testId="savings-page">
      <SavedBar kind="savings_goal" selected={pid} onSelect={(id, inp) => { setPid(id); if (inp) setI({ ...DEFAULTS, ...inp }); }} inputs={i} onReset={() => setI(DEFAULTS)} defaultName={CATS.find((c) => c[0] === i.category)?.[1]} />
      <Card title="Your goal" testId="savings-inputs">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <SelectField id="sav-category" label="Goal" value={i.category} onChange={set("category")} options={CATS} />
          <NumField id="sav-target" label="Savings target" value={i.target} onChange={set("target")} />
          <NumField id="sav-current" label="Current savings" value={i.current} onChange={set("current")} hint={contributed ? `+ ${money(contributed)} recorded contributions` : undefined} />
          <NumField id="sav-date" label="Target date" type="date" value={i.target_date} onChange={set("target_date")} />
          <SelectField id="sav-frequency" label="I'll contribute" value={i.frequency} onChange={set("frequency")} options={[["weekly", "Weekly"], ["biweekly", "Every 2 weeks"], ["monthly", "Monthly"]]} />
          <NumField id="sav-rate" label="Interest rate (optional, %)" value={i.rate} onChange={set("rate")} />
          <NumField id="sav-contribution" label={`My planned contribution per ${per} (optional)`} value={i.contribution} onChange={set("contribution")} />
        </div>
      </Card>
      {calc.error && <p className="text-sm text-red-400" data-testid="fin-calc-error">{formatApiErrorDetail(calc.error.response?.data?.detail)}</p>}
      {r && (
        <Card testId="savings-results">
          <Progress pct={r.progress_pct} />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
            <Stat label={`Needed per ${per}`} value={money(r.required_contribution)} tone="good" testId="sav-required" sub={`${r.periods} contributions`} />
            <Stat label="Saved so far" value={money(r.current)} testId="sav-current-total" />
            <Stat label="Still to save" value={money(r.remaining)} />
            {r.projected_date ? <Stat label="With your plan you reach it" value={fmtDate(r.projected_date)} tone={r.on_track ? "good" : "bad"} testId="sav-projected" sub={r.on_track ? "On track" : "Behind target date"} />
              : <Stat label="Target date" value={fmtDate(i.target_date)} />}
          </div>
        </Card>
      )}
      <PaymentHistory pid={pid} txns={txns} title="Recorded contributions" />
      <Assumptions>Interest, if entered, is compounded monthly and is not guaranteed. Contributions are assumed at the end of each period.</Assumptions>
    </FinShell>
  );
}
